import React, { useMemo } from 'react'
import { Bar } from 'react-chartjs-2'
import {
  chartjsTitleProperties,
  chartjsScaleLabelProperties,
  chartjsDefaultOptions,
  chartjsDefaultArguments
} from '../../../common/Constants'
import { identity } from '../../../common/Units'
import PropTypes from 'prop-types'

const HEIGHT = 238

const StackedBarChart = (props) => {
  const datasets = useMemo(() => {
    const series = Array.from(props.datasets)
    if (!props.scaled || series.length === 0) return series

    const totals = Array(series[0].data.length).fill(0)
    for (const serie of series) {
      for (const [idx, value] of serie.data.entries()) {
        totals[idx] += value
      }
    }
    return series.map(serie => ({
      ...serie,
      data: serie.data.map((value, idx) => totals[idx] ? 100.0 * value / totals[idx] : value)
    }))
  }, [props.datasets, props.scaled])

  const data = {
    labels: props.labels,
    datasets
  }

  const { plugins: extraPlugins, ...extraOptions } = props.options || {}
  const options = {
    ...chartjsDefaultOptions,
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      title: {
        text: props.title,
        ...chartjsTitleProperties
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: props.tooltipCallbacks
      },
      ...extraPlugins
    },
    scales: {
      x: {
        stacked: props.stacked,
        min: props.range?.[0],
        max: props.range?.[1],
        ticks: {
          font: { size: props.xAxisFontSize }
        }
      },
      y: {
        stacked: props.stacked,
        min: props.yMin,
        max: props.yMax,
        title: {
          text: props.yLabel,
          ...chartjsScaleLabelProperties
        },
        ticks: {
          callback: props.yTickCallback,
          precision: props.precision
        }
      }
    },
    ...extraOptions
  }
  return (
    <div className='chartjs-container'>
      <Bar {...chartjsDefaultArguments} height={HEIGHT} data={data} options={options} redraw />
    </div>
  )
}

StackedBarChart.propTypes = {
  title: PropTypes.string,
  labels: PropTypes.array,
  datasets: PropTypes.array,
  yLabel: PropTypes.string,
  yTickCallback: PropTypes.func,
  tooltipCallbacks: PropTypes.object,
  yMin: PropTypes.number,
  yMax: PropTypes.number,
  range: PropTypes.array,
  // Override/add additional options
  options: PropTypes.object,
  stacked: PropTypes.bool,
  xAxisFontSize: PropTypes.number,
  precision: PropTypes.number
}

StackedBarChart.defaultProps = {
  yLabel: '',
  yTickCallback: identity,
  // Warning: If this is undefined for one chart tooltips will break for all charts in the page.
  tooltipCallbacks: {},
  stacked: true,
  xAxisFontSize: 17.5
}

export default StackedBarChart
